import { Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { useEffect, useRef } from "react";
import useAuth from "../hooks/useAuth";
import AlertComponent from "../helpers/alert/AlertComponent";

/**
 * RoleGuard
 * -----------------------------------------------------------------------------
 * OBJETIVO
 * - Restringir el acceso a rutas privadas a un conjunto de roles permitidos.
 * - Si el rol del usuario autenticado no está en `allowedRoles`, se redirige
 *   al dashboard (`/admin`) y se muestra una alerta de acceso denegado.
 *
 * USO
 *   <Route element={<RoleGuard allowedRoles={[RolesEnum.ADMIN, ...]} />}>
 *     <Route path={...} element={<Pagina />} />
 *   </Route>
 *
 * NOTA
 * - Reenvía el outletCtx (igual que SupplierDocsGuard) porque muchas páginas
 *   consumen `const { userAuth } = useOutletContext();`.
 */
export const RoleGuard = ({ allowedRoles = [] }) => {
    const location = useLocation();
    const outletCtx = useOutletContext();
    const { auth } = useAuth();
    const alertedRef = useRef(false);

    const rolId = auth?.rol_id;
    const isAllowed = rolId != null && allowedRoles.includes(rolId);

    useEffect(() => {
        if (isAllowed) {
            alertedRef.current = false;
            return;
        }
        if (rolId == null) return; // aún no hay auth resuelta
        if (alertedRef.current) return;
        alertedRef.current = true;
        AlertComponent.warning("Acceso denegado", "No tienes permisos para acceder a este módulo.");
    }, [isAllowed, rolId]);

    // Mientras no haya rol resuelto, no redirigimos (evita expulsar en la carga inicial)
    if (rolId == null) {
        return <div>Cargando...</div>;
    }

    if (!isAllowed) {
        return <Navigate to="/admin" replace state={{ from: location.pathname }} />;
    }

    return <Outlet context={outletCtx} />;
};